/**
 * Normalise `booking.requirements` to a plain object (handles JSON string from API).
 */
export function parseBookingRequirements(booking) {
  const raw = booking?.requirements;
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' && !Array.isArray(p) ? { ...p } : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  return {};
}

/**
 * True when the booking has at least one add-on / package line or positive add-ons total.
 */
export function bookingHasAddOns(booking) {
  if (!booking || typeof booking !== 'object') return false;
  if (booking.has_add_ons === true) return true;
  if (booking.has_add_ons === false) return false;
  const req = parseBookingRequirements(booking);
  const fromBooking = Array.isArray(booking.packages) ? booking.packages : null;
  const fromReq = Array.isArray(req.packages) ? req.packages : [];
  const pkgs = fromBooking && fromBooking.length > 0 ? fromBooking : fromReq;

  if (Array.isArray(pkgs) && pkgs.length > 0) {
    const hasLine = pkgs.some((p) => {
      if (!p || typeof p !== 'object') return false;
      const qtyRaw = p.quantity ?? p.qty;
      const qty =
        qtyRaw === '' || qtyRaw == null ? NaN : Number(qtyRaw);
      if (Number.isFinite(qty) && qty > 0) return true;
      const raw = p.amount ?? p.total;
      if (raw == null || String(raw).toUpperCase() === 'TBC') return false;
      const n =
        typeof raw === 'number' ? raw : Number.parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(n) && n > 0) return true;
      return false;
    });
    if (hasLine) return true;
  }

  const rawTotal = booking.add_ons_total ?? req.add_ons_total;
  if (rawTotal != null && String(rawTotal).toUpperCase() !== 'TBC' && String(rawTotal) !== '') {
    const n =
      typeof rawTotal === 'number'
        ? rawTotal
        : Number.parseFloat(String(rawTotal).replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(n) && n > 0) return true;
  }

  return false;
}

/**
 * Map venue add-on catalog items by id for name lookup on legacy package lines.
 */
export function buildAddOnCatalogById(catalogItems) {
  const map = new Map();
  (catalogItems || []).forEach((item) => {
    const id = item?.id ?? item?.item_id ?? item?.add_on_item_id;
    if (id == null) return;
    const label = item?.name ?? item?.item_name ?? item?.title;
    if (label) map.set(String(id), String(label).trim());
  });
  return map;
}

/**
 * Resolve a human-readable label for a package / add-on line on a booking.
 */
export function resolvePackageLineName(line, catalogById) {
  if (!line || typeof line !== 'object') return 'Add-on';

  const direct =
    line.name ??
    line.package_name ??
    line.title ??
    line.item_name ??
    line.item?.name ??
    line.item?.item_name ??
    line.package?.name ??
    line.package?.package_name;
  const trimmed = direct == null ? '' : String(direct).trim();
  if (trimmed && trimmed.toLowerCase() !== 'add-on') return trimmed;

  const catalogId =
    line.package_id ??
    line.item_id ??
    line.add_on_item_id ??
    line.item?.id ??
    line.item?.item_id;
  if (catalogId != null && catalogById) {
    const fromCatalog = catalogById.get(String(catalogId));
    if (fromCatalog) return fromCatalog;
  }

  return trimmed || 'Add-on';
}

/**
 * Package lines from attached booking.packages (table) first.
 * Merge names from requirements.packages when the table row is unnamed.
 */
export function getBookingPackageLines(booking) {
  const req = parseBookingRequirements(booking);
  const fromReq = Array.isArray(req.packages) ? req.packages : [];
  const fromBooking = Array.isArray(booking?.packages) ? booking.packages : null;
  if (fromBooking && fromBooking.length > 0) {
    if (fromReq.length === 0) return fromBooking;
    return fromBooking.map((line, index) => {
      const sibling = fromReq[index];
      if (!sibling || typeof sibling !== 'object') return line;
      const name =
        line.name ??
        line.package_name ??
        sibling.name ??
        sibling.package_name ??
        line.item_name ??
        sibling.item_name;
      return name ? { ...sibling, ...line, name } : { ...sibling, ...line };
    });
  }
  return fromReq;
}

/**
 * Recurring booking flags stored in requirements JSON (platform enquiry form).
 */
export function getBookingRecurringInfo(booking) {
  if (booking?.is_recurring === true) {
    const req = parseBookingRequirements(booking);
    const schedule = String(req.recurring_schedule || req.recurrence_label || '').trim();
    const frequency = String(req.booking_frequency || req.recurrence || '').trim();
    const label = schedule || (frequency && frequency !== 'One-off' ? frequency : '');
    return { isRecurring: true, schedule, frequency, label };
  }
  if (booking?.is_recurring === false) {
    return { isRecurring: false, schedule: '', frequency: '', label: '' };
  }
  const req = parseBookingRequirements(booking);
  const isRecurring = Boolean(req.is_recurring || req.recurring || req.recurrence);
  const schedule = String(req.recurring_schedule || '').trim();
  const frequency = String(req.booking_frequency || '').trim();
  const label = schedule || (frequency && frequency !== 'One-off' ? frequency : '');
  return { isRecurring, schedule, frequency, label };
}
